/**
 * Tracks which analyses this browser has opened, and when, so the home page can
 * surface the ones the user actually works with.
 *
 * Local to the browser on purpose — there's no server-side "last viewed" for an
 * analysis, and this only needs to be good enough to order a short list.
 */

const STORAGE_KEY = 'pliable.recentAnalyses';

/** Keep the map small; anything older than this many entries gets dropped. */
const MAX_TRACKED = 50;

/** analysis id → ISO timestamp of the last time it was opened. */
export type AnalysisAccessTimes = Record<string, string>;

export function getAnalysisAccessTimes(): AnalysisAccessTimes {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as AnalysisAccessTimes;
  } catch {
    // Corrupt or unreadable storage shouldn't break the page.
    return {};
  }
}

export function recordAnalysisAccess(analysisId: string): void {
  if (typeof window === 'undefined' || !analysisId) return;
  try {
    const times = { ...getAnalysisAccessTimes(), [analysisId]: new Date().toISOString() };

    // Trim the oldest entries once we're over the cap.
    const ids = Object.keys(times);
    if (ids.length > MAX_TRACKED) {
      const keep = ids
        .sort((a, b) => Date.parse(times[b]) - Date.parse(times[a]))
        .slice(0, MAX_TRACKED);
      const trimmed: AnalysisAccessTimes = {};
      for (const id of keep) trimmed[id] = times[id];
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(times));
  } catch {
    // Storage full or blocked — ordering just falls back to alphabetical.
  }
}
